import React, { useEffect, useState } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from './ui/dialog';
import { ClipboardCheck, Plus, Link2Off, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { inspectionTemplatesApi } from '../services/api';

/**
 * Per-asset whitelist of inspection templates that anonymous Public-QR
 * scanners are allowed to run against this asset.
 *
 * Mirrors AssetErrorCodeLibrariesCard so admins get a consistent linking UX:
 *  – self-managing local state (no parent refresh required)
 *  – picker dialog lists only `published` templates not already linked
 *  – unlink is a single-click red icon, no extra confirmation (low-risk op)
 */
export const AssetPublicInspectionsCard = ({ asset }) => {
  const [linkedIds, setLinkedIds] = useState(asset?.public_inspection_template_ids || []);
  const [allTemplates, setAllTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [picker, setPicker] = useState(false);
  const [busyId, setBusyId] = useState(null);

  useEffect(() => {
    setLinkedIds(asset?.public_inspection_template_ids || []);
  }, [asset?.id, asset?.public_inspection_template_ids]);

  useEffect(() => {
    (async () => {
      try {
        const res = await inspectionTemplatesApi.list();
        setAllTemplates(Array.isArray(res.data) ? res.data : []);
      } catch {
        // Quietly fail — empty list.
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Published templates only — drafts must never be exposed to public scanners.
  const publishable = allTemplates.filter(t => !t.status || t.status === 'published');
  const linked = publishable.filter(t => linkedIds.includes(t.id));
  const linkable = publishable.filter(t => !linkedIds.includes(t.id));

  const unlink = async (tpl) => {
    setBusyId(tpl.id);
    try {
      await inspectionTemplatesApi.unlinkFromAsset(asset.id, tpl.id);
      setLinkedIds(prev => prev.filter(id => id !== tpl.id));
      toast.success(`Unlinked "${tpl.name}"`);
    } catch {
      toast.error('Unlink failed');
    } finally {
      setBusyId(null);
    }
  };

  const link = async (tpl) => {
    setBusyId(tpl.id);
    try {
      await inspectionTemplatesApi.linkToAsset(asset.id, tpl.id);
      setLinkedIds(prev => [...prev, tpl.id]);
      toast.success(`"${tpl.name}" is now available via the public QR`);
      setPicker(false);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Link failed');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Card className="shadow-none" data-testid="asset-public-inspections-card">
      <CardContent className="p-5 space-y-3">
        <div className="flex items-center justify-between">
          <div className="as-section">
            <ClipboardCheck className="w-3.5 h-3.5 text-[#C9A400]" />
            Public Inspections
            {linked.length > 0 && (
              <Badge className="lm-count ml-0.5 h-5 min-w-[20px] justify-center px-1.5 text-[11px]">{linked.length}</Badge>
            )}
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={() => setPicker(true)}
            disabled={loading}
            data-testid="link-public-inspection-btn"
          >
            <Plus className="w-4 h-4 mr-1" />
            Add template
          </Button>
        </div>

        <p className="text-xs text-muted-foreground leading-relaxed">
          Anyone scanning this asset's QR can run the inspections listed below.
          Operators are required to enter their name + mobile before submitting.
          Failures auto-raise defects flagged <em>via QR</em>.
        </p>

        {loading ? (
          <p className="text-xs text-muted-foreground">Loading…</p>
        ) : linked.length === 0 ? (
          <div className="border border-dashed border-border rounded-md p-4 text-center" data-testid="asset-public-insp-empty">
            <p className="text-xs text-muted-foreground">
              No public inspections enabled. Click <strong>Add template</strong> to expose one.
            </p>
          </div>
        ) : (
          <ul className="space-y-2">
            {linked.map(tpl => (
              <li
                key={tpl.id}
                className="flex items-center justify-between gap-3 p-3 rounded-md bg-[#FBFAF7] border border-border"
                data-testid={`asset-public-insp-linked-${tpl.id}`}
              >
                <div className="min-w-0">
                  <p className="font-medium text-sm truncate">{tpl.name}</p>
                  <p className="text-xs text-muted-foreground">
                    {tpl.category || 'general'} · {tpl.checklist?.length ?? 0} items
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => unlink(tpl)}
                  disabled={busyId === tpl.id}
                  data-testid={`asset-public-insp-unlink-${tpl.id}`}
                  title="Unlink"
                >
                  {busyId === tpl.id ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Link2Off className="w-4 h-4 text-red-500" />
                  )}
                </Button>
              </li>
            ))}
          </ul>
        )}
      </CardContent>

      {picker && (
        <Dialog open onOpenChange={(v) => !v && setPicker(false)}>
          <DialogContent data-testid="asset-public-insp-picker-dialog">
            <DialogHeader>
              <DialogTitle>
                Expose an inspection on {asset.unit_number || asset.name}
              </DialogTitle>
            </DialogHeader>
            <div className="py-2">
              {linkable.length === 0 ? (
                <p className="text-sm text-muted-foreground py-4">
                  No additional published templates. Go to{' '}
                  <span className="text-foreground">Inspections → Templates</span> to create one.
                </p>
              ) : (
                <ul className="space-y-2 max-h-80 overflow-y-auto">
                  {linkable.map(tpl => (
                    <li
                      key={tpl.id}
                      className="flex items-center justify-between gap-3 p-3 rounded-md bg-[#FBFAF7] border border-border"
                    >
                      <div className="min-w-0">
                        <p className="font-medium text-sm truncate">{tpl.name}</p>
                        <p className="text-xs text-muted-foreground">
                          {tpl.category || 'general'} · {tpl.checklist?.length ?? 0} items
                        </p>
                      </div>
                      <Button
                        size="sm"
                        onClick={() => link(tpl)}
                        disabled={busyId === tpl.id}
                        data-testid={`asset-public-insp-link-option-${tpl.id}`}
                      >
                        {busyId === tpl.id ? (
                          <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                        ) : (
                          <Plus className="w-4 h-4 mr-1" />
                        )}
                        Expose
                      </Button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setPicker(false)}>Close</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </Card>
  );
};
